<template>
  <div class="pdf-container">
    <PdfTopbar
      :title="fileName || ''"
      :source-url="selectedReference?.sourceUrl || ''"
      :external-id="selectedReference?.externalId || ''"
      :pdf-src="src"
      :displayVeevaButton="displayVeevaButton"
    />
    <div class="pdf-viewer-container">
      <BbLoadingIndicator v-if="isLoading" class="loading-indicator" />
      <VuePdfEmbed
        ref="pdfViewer"
        class="pdf-viewer"
        :style="{ zoom: zoom, overflow: 'auto' }"
        :key="localCurrentPage"
        :source="src"
        :page="localCurrentPage"
        :scale="7"
        :width="500"
        @loaded="setTotalPages"
        @error="(err) => console.log('PDF load error:', err)"
        @loading-failed="(err) => console.log('PDF load error:', err)"
        @rendering-failed="(err) => console.log('PDF render error:', err)"
      />
    </div>
    <PdfActionsGadget
      :current-page="localCurrentPage"
      :total-pages="totalPages"
      @update:zoomIn="zoomIn"
      @update:zoomOut="zoomOut"
      @update:prevPage="prevPage"
      @update:nextPage="nextPage"
    />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import VuePdfEmbed from 'vue-pdf-embed/dist/index.essential.mjs';
import PdfActionsGadget from '~/components/pdfViewer/PdfActionsGadget';
import PdfTopbar from '~/components/pdfViewer/PdfTopbar';
import useDocuments from '~/composables/useDocuments';
import useTranslation from '~/composables/useTranslation';
import useModals from '~/composables/useModals';
import { addLanguageToFileName } from '~/utils/document-utils';
import validateUrl from '~/utils/url';

export default {
  components: { PdfTopbar, PdfActionsGadget, VuePdfEmbed },
  props: {
    open: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const { getPdfUrl, selectedReference } = useDocuments();
    const { currentLanguageCode } = useTranslation();
    const pdfViewer = ref([]);
    const totalPages = ref(0);
    const pdfDocument = ref(null);
    const zoom = ref(2);
    const src = ref('');
    const isLoading = ref(true);

    // state for pdf viewer and start page
    const { previewDocumentState } = useModals();
    const localCurrentPage = ref(previewDocumentState.value.startPage);
    const fileName = computed(() =>
      currentLanguageCode.value
        ? addLanguageToFileName(selectedReference.value.name, currentLanguageCode.value)
        : selectedReference.value.name,
    );
    const displayVeevaButton = computed(
      () =>
        selectedReference.value.type !== 'documentTranslation' && selectedReference.value.externalId.includes('veeva'),
    );

    /* const executeSearch = () => {
      const snippetToHighlight = decodeFromBase64(selectedReference.value.snippet);
      if (snippetToHighlight) {
        setTimeout(() => {
          pdfViewer.value.pdfFindController.executeCommand('find', {
            caseSensitive: false,
            findPrevious: undefined,
            highlightAll: true,
            phraseSearch: true,
            query: snippetToHighlight,
          });
        }, 300); // Adjust delay as necessary
      }
    }; */

    const loadDocument = (externalId) => {
      if (selectedReference.value && currentLanguageCode.value === selectedReference.value.sourceLanguage) {
        getPdfUrl(externalId, '').then((url) => {
          src.value = validateUrl(url) ? url : '';
        });
      } else {
        getPdfUrl(externalId, currentLanguageCode.value).then((url) => {
          src.value = validateUrl(url) ? url : '';
        });
      }
    };

    const nextPage = () => (localCurrentPage.value < totalPages.value ? localCurrentPage.value++ : 1);

    const prevPage = () => (localCurrentPage.value > 1 ? localCurrentPage.value-- : 1);

    const zoomIn = () => {
      zoom.value = Math.min(zoom.value + 0.1, 15);
    };

    const zoomOut = () => {
      zoom.value = Math.max(zoom.value - 0.1, 2);
    };

    const setTotalPages = (event) => {
      totalPages.value = event.numPages;
      isLoading.value = false;
    };

    // need to execute the search once the pdf is loaded or currentPage is change
    /* watch([localCurrentPage, isPdfUrlLoaded], () => {
      executeSearch();
    }); */

    watch(currentLanguageCode, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        loadDocument(selectedReference.value.externalId, newVal);
      }
    });

    watch(
      () => selectedReference.value && selectedReference.value.externalId,
      (newId, oldId) => {
        if (newId !== oldId) {
          loadDocument(newId);
        }
      },
      { immediate: true },
    );

    return {
      isLoading,
      pdfViewer,
      nextPage,
      localCurrentPage,
      pdfDocument,
      prevPage,
      selectedReference,
      totalPages,
      zoom,
      setTotalPages,
      src,
      zoomIn,
      zoomOut,
      fileName,
      displayVeevaButton,
    };
  },
};
</script>

<style scoped lang="scss">
.pdf-container {
  /* Style your dialog/panel here */
  display: flex;
  flex-direction: column;
  height: 100vh; /* Take full viewport height */
  overflow: hidden; /* Prevent scrolling */
  width: 100%;
  position: fixed; /* Use fixed positioning */
  top: 0;
  left: 0;
  z-index: 1000; /* Ensure it's above other content */
  .pdf-viewer-container {
    flex-grow: 1;
    display: flex;
    justify-content: center; /* Center horizontally */
    align-items: center; /* Center vertically */
    //overflow: hidden; /* Ensure no scrolling within the PDF viewer itself */
    overflow: auto;
    background-color: $bb-neutral-0;
    padding-top: $bb-spacing-2;

    .loading-indicator {
      background-color: $bb-neutral-0;
    }

    .pdf-viewer {
      max-width: 100%; /* Ensure the PDF viewer does not overflow its container */
      max-height: 100%; /* Adapt based on the actual size of the viewer */
      :deep(.highlight) {
        background-color: yellow !important;
      }
    }
  }
}
</style>
